/* eslint-disable @typescript-eslint/no-shadow */
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getMessageFromError } from '@/core';
import { FileUploader } from '@/common';
import { createClientAnswer } from '../api/createClientAnswer';

type AddClientAnswerFormType = {
  info: string;
};

type ClientAnswerModalProps = {
  visitId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  answerType: 'rec' | 'nonConf' | null;
};

const ClientAnswerModal = ({ isOpen, setIsOpen, visitId, answerType }: ClientAnswerModalProps) => {
  const theme = useTheme();
  const [uploadedFiles, setUploadedFiles] = useState<FormData>();
  const [show, setShow] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<AddClientAnswerFormType>({
    mode: 'onTouched',
    defaultValues: {
      info: '',
    },
  });

  const addClientAnswer = useMutation({
    mutationFn: createClientAnswer,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully added answer', { defaultValue: 'Successfully added answer' }),
        { variant: 'success' }
      );
      setIsOpen(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
      setUploadedFiles(undefined);
    }
  }, [isOpen, form]);

  const baseSubmit = (data: AddClientAnswerFormType) => {
    if (!uploadedFiles) {
      form.setError('root', {
        message: t('File is required', {
          defaultValue: 'File is required.',
        }).toString(),
      });
      return;
    }
    if (!answerType) {
      return;
    }
    uploadedFiles.append('info', data.info);
    addClientAnswer.mutate({ id: visitId, formData: uploadedFiles, answerType });
  };

  const handleClose = () => {
    form.reset();
    setIsOpen(false);
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      {answerType === 'rec' && (
        <DialogTitle id="responsive-dialog-title">
          {t('Add a answer on recomendations', {
            defaultValue: 'Add a answer on recomendations',
          })}
        </DialogTitle>
      )}
      {answerType === 'nonConf' && (
        <DialogTitle id="responsive-dialog-title">
          {t('Add a answer on noncompliances', {
            defaultValue: 'Add a answer on noncompliances',
          })}
        </DialogTitle>
      )}
      <DialogContent>
        <Box
          component="form"
          onSubmit={form.handleSubmit(baseSubmit)}
          noValidate
          sx={{ mt: '1rem' }}
        >
          <Controller
            control={form.control}
            name="info"
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Note', { defaultValue: 'Note' })}
                fullWidth
                multiline
                rows={6}
                error={!!form.formState.errors.info}
                helperText={form.formState.errors.info?.message?.toString()}
              />
            )}
          />
          <Dialog
            fullScreen={fullScreen}
            open={show}
            onClose={() => setShow(false)}
            aria-labelledby="responsive-dialog-title"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5">
                {t('Upload files', { defaultValue: 'Upload files' })}
              </Typography>
              <CloseIcon
                sx={{ fontSize: '2rem', cursor: 'pointer' }}
                onClick={() => setShow(false)}
              />
            </DialogTitle>
            <DialogContent>
              <FileUploader
                // eslint-disable-next-line consistent-return
                onFileUpload={async (file) => {
                  const formData = new FormData();
                  formData.append('title', file.file.name);
                  formData.append('attachments', file.file);
                  setUploadedFiles(formData);
                }}
                onUploadFinished={(allFilesSuccessfullyUploaded) => {
                  if (allFilesSuccessfullyUploaded) {
                    form.clearErrors('root');
                    setShow(false);
                  }
                }}
              />
            </DialogContent>
          </Dialog>

          {!uploadedFiles && (
            <Button
              variant="contained"
              type="button"
              fullWidth
              sx={{ mt: '1rem' }}
              onClick={() => setShow(true)}
            >
              {t('Add files', { defaultValue: 'Add files' })}
            </Button>
          )}
          {uploadedFiles && (
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.grey[200],
                display: 'flex',
                alignItems: 'center',
                p: '1rem',
                borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
                position: 'relative',
                mt: '1rem',
              })}
            >
              <Box
                sx={(theme) => ({
                  position: 'absolute',
                  height: '100%',
                  backgroundColor: 'success.light',
                  width: '0.3rem',
                  left: 0,
                  top: 0,
                  borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                  borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
                })}
              />
              <InsertDriveFileOutlinedIcon sx={{ fontSize: '2rem' }} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  ml: '1rem',
                }}
              >
                <Typography>{uploadedFiles && uploadedFiles?.get('title')?.toString()}</Typography>
              </Box>
            </Box>
          )}
          <Button variant="contained" type="submit" fullWidth sx={{ mt: '1rem' }}>
            {t('Submit', { defaultValue: 'Submit' })}
          </Button>
          {form.formState.errors?.root && (
            <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
          )}
          <Button variant="outlined" fullWidth sx={{ mt: '1rem' }} onClick={handleClose}>
            {t('Cancel', { defaultValue: 'Cancel' })}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ClientAnswerModal;
